import GlobalVariables from "./globalVariables";

const BASE_PATH = "/computerCronJob/computer-cron-job";
const HEADERS = { "Content-Type": "application/json" };

class ComputerCronJobModel {
  static getBaseURL() {
    return GlobalVariables.getAPIURL() + BASE_PATH;
  }

  static async list(uid) {
    if (!uid) return [];

    try {
      const res = await fetch(
        `${ComputerCronJobModel.getBaseURL()}/list?uid=${encodeURIComponent(uid)}`,
        { headers: HEADERS }
      );
      const data = await res.json();
      if (data.success && Array.isArray(data.jobs)) {
        return data.jobs;
      }
      return [];
    } catch (err) {
      console.warn("[ComputerCronJob] list failed:", err);
      return [];
    }
  }

  static async create(payload) {
    try {
      const res = await fetch(`${ComputerCronJobModel.getBaseURL()}/create`, {
        method: "POST",
        headers: HEADERS,
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      return data.success ? data.job : null;
    } catch (err) {
      console.warn("[ComputerCronJob] create failed:", err);
      return null;
    }
  }

  static async update(jobId, uid, updates) {
    try {
      const res = await fetch(`${ComputerCronJobModel.getBaseURL()}/update`, {
        method: "POST",
        headers: HEADERS,
        body: JSON.stringify({ jobId, uid, ...updates }),
      });
      const data = await res.json();
      return data.success ? data.job : null;
    } catch (err) {
      console.warn("[ComputerCronJob] update failed:", err);
      return null;
    }
  }

  static async delete(jobId, uid) {
    try {
      const res = await fetch(`${ComputerCronJobModel.getBaseURL()}/delete`, {
        method: "POST",
        headers: HEADERS,
        body: JSON.stringify({ jobId, uid }),
      });
      const data = await res.json();
      return !!data.success;
    } catch (err) {
      console.warn("[ComputerCronJob] delete failed:", err);
      return false;
    }
  }

  static async toggle(jobId, uid) {
    try {
      const res = await fetch(`${ComputerCronJobModel.getBaseURL()}/toggle`, {
        method: "POST",
        headers: HEADERS,
        body: JSON.stringify({ jobId, uid }),
      });
      const data = await res.json();
      return data.success ? data : null;
    } catch (err) {
      console.warn("[ComputerCronJob] toggle failed:", err);
      return null;
    }
  }

  static async runNow(jobId, uid) {
    try {
      const res = await fetch(`${ComputerCronJobModel.getBaseURL()}/run-now`, {
        method: "POST",
        headers: HEADERS,
        body: JSON.stringify({ jobId, uid }),
      });
      const data = await res.json();
      return data.success ? data : null;
    } catch (err) {
      console.warn("[ComputerCronJob] runNow failed:", err);
      return null;
    }
  }

  static async get(jobId) {
    try {
      const res = await fetch(
        `${ComputerCronJobModel.getBaseURL()}/get?jobId=${encodeURIComponent(jobId)}`,
        { headers: HEADERS }
      );
      const data = await res.json();
      return data.success ? data.job : null;
    } catch (err) {
      console.warn("[ComputerCronJob] get failed:", err);
      return null;
    }
  }

  /**
   * Build a human-readable description of a schedule object.
   */
  static describeSchedule(schedule) {
    if (!schedule) return "No schedule";

    switch (schedule.kind) {
      case "cron": {
        const parts = (schedule.expr || "").split(" ");
        if (parts.length < 5) return schedule.expr;

        const [min, hour, , , dow] = parts;
        const time = `${hour.padStart(2, "0")}:${min.padStart(2, "0")}`;
        const tz = schedule.tz || "America/Los_Angeles";
        const tzShort = tz.includes("Los_Angeles") ? "PT" : tz.includes("New_York") ? "ET" : tz.includes("Chicago") ? "CT" : tz;

        if (dow === "*") return `Daily at ${time} ${tzShort}`;
        if (dow === "1-5") return `Weekdays at ${time} ${tzShort}`;
        return `${schedule.expr} (${tzShort})`;
      }

      case "every": {
        const ms = schedule.everyMs || 0;
        if (ms >= 86400000 && ms % 86400000 === 0) {
          const days = ms / 86400000;
          return days === 1 ? "Every day" : `Every ${days} days`;
        }
        if (ms >= 3600000 && ms % 3600000 === 0) {
          const hours = ms / 3600000;
          return hours === 1 ? "Every hour" : `Every ${hours} hours`;
        }
        const mins = Math.round(ms / 60000);
        return mins === 1 ? "Every minute" : `Every ${mins} minutes`;
      }

      case "at": {
        try {
          const d = new Date(schedule.at);
          return `Once on ${d.toLocaleDateString()} at ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
        } catch {
          return `Once at ${schedule.at}`;
        }
      }

      default:
        return "Unknown schedule";
    }
  }

  /**
   * Return a status label and color for a job.
   */
  static getJobStatus(job) {
    if (!job.enabled) {
      if ((job.state?.consecutiveErrors || 0) >= (job.maxConsecutiveErrors || 5)) {
        return { label: "Auto-disabled", color: "#d32f2f" };
      }
      return { label: "Disabled", color: "#9e9e9e" };
    }

    const lastStatus = job.state?.lastStatus;
    if (lastStatus === "ok") return { label: "Active", color: "#4caf50" };
    if (lastStatus === "queued") return { label: "Queued", color: "#ff9800" };
    if (lastStatus === "error") return { label: "Error", color: "#d32f2f" };
    if (lastStatus === "conflict_timeout") return { label: "Skipped", color: "#ff9800" };

    return { label: "Pending", color: "#2196f3" };
  }
}

export default ComputerCronJobModel;
